import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Handshake, Calendar, Heart, Settings, LogOut, Menu, X, Home, MessageCircle, LifeBuoy, Bell, UserPlus, Trash2, FileText, Tag, BookOpen } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e194f4c5-8d32-4ac0-8243-34dcb9b16c4c/artifacts/i9igc7w1_FBABBFB0-B86A-40AF-A319-0D7428AF7412.png";
const APP_URL = typeof window !== 'undefined' ? window.location.origin : 'https://yorm.app';

export default function CustomerHeader() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      
      try {
        const res = await axios.get(`${API}/messages/unread-count?token=${token}`);
        setUnreadCount(res.data?.count || 0);
      } catch (e) {
        console.error("Failed to fetch unread count", e);
      }
    };

    fetchUnreadCount();
    
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    // Instant refresh when any chat page marks its thread read or a
    // fresh message lands — keeps the header bubble Apple-crisp.
    const onMsgSync = () => fetchUnreadCount();
    window.addEventListener("yorm:messages-updated", onMsgSync);
    const onVis = () => { if (!document.hidden) fetchUnreadCount(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      window.removeEventListener("yorm:messages-updated", onMsgSync);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [token]);

  useEffect(() => {
    const fetchSupportUnreadCount = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/support/unread-count?token=${token}`);
        setSupportUnreadCount(res.data?.unread_count || 0);
      } catch (e) {
        // Ignore errors
      }
    };
    fetchSupportUnreadCount();
    const interval = setInterval(fetchSupportUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/user/notifications?token=${token}`);
        setNotifications(res.data?.notifications || []);
        setNotifUnreadCount(res.data?.unread_count || 0);
      } catch (e) {
        // Ignore errors
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const markAsRead = async (notifId, notif) => {
    try {
      await axios.post(`${API}/user/notifications/${notifId}/read?token=${token}`);
      setNotifications(prev => prev.map(n => n.id === notifId ? {...n, read: true} : n));
      setNotifUnreadCount(prev => Math.max(0, prev - 1));
      
      // Navigate based on notification type and data
      if (notif?.data?.booking_id) {
        navigate(`/customer/chat/${notif.data.booking_id}?from=bell`);
      } else if (notif?.type === "booking" || notif?.type === "new_booking") {
        navigate("/customer/bookings");
      } else if (notif?.type === "message" || notif?.type === "new_message") {
        navigate("/customer/chats");
      }
    } catch (e) {
      // Ignore
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/user/notifications/read-all?token=${token}`);
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setNotifUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error("Failed to mark all as read");
    }
  };

  const deleteNotification = async (e, notifId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/user/notifications/${notifId}?token=${token}`);
      const notif = notifications.find(n => n.id === notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      if (notif && !notif.read) {
        setNotifUnreadCount(prev => Math.max(0, prev - 1));
      }
      toast.success("Notification cleared");
    } catch (e) {
      toast.error("Failed to clear notification");
    }
  };

  const deleteAllNotifications = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    try {
      await axios.delete(`${API}/user/notifications?token=${token}`);
      setNotifications([]);
      setNotifUnreadCount(0);
      toast.success("All notifications cleared");
    } catch (e) {
      toast.error("Failed to clear notifications");
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleInviteFriend = async () => {
    const shareData = {
      title: 'Join Yorm',
      text: 'Find trusted professionals near you! Download the Yorm app and book services anywhere, anytime.',
      url: APP_URL
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to clipboard
          await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
          toast.success("Invite link copied to clipboard!");
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      toast.success("Invite link copied to clipboard!");
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const navLinks = [
    { path: "/customer", label: "Home", icon: Home, exact: true },
    { path: "/search", label: "Shared Services", icon: Handshake },
    { path: "/customer/bookings", label: "My Bookings", icon: Calendar },
    { path: "/customer/chats", label: "Messages", icon: MessageCircle, testId: "chats-nav-btn", alsoActive: "/customer/chat", badge: unreadCount },
    { path: "/customer/favorites", label: "Favorites", icon: Heart, testId: "favorites-nav-link" },
  ];

  const isExactActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 ${mobileMenuOpen ? "z-[1150]" : "z-[1100]"} apple-bar apple-bar-top`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <Link to="/customer" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Yorm" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
            <span className="text-xl sm:text-2xl text-[#222222] logo-text">Yorm</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={link.testId}
                  className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-full transition flex items-center gap-2 ${
                    isExactActive(link.path, link.exact) || (link.alsoActive && isActive(link.alsoActive))
                      ? "bg-[#4F988E]/10 text-[#4F988E]"
                      : "text-[#222222] hover:bg-gray-100"
                  }`}
                >
                  <span className="relative inline-flex">
                    <Icon size={16} />
                    {link.badge > 0 && (
                      <span className="absolute -top-2 -right-2.5 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1" data-testid="chats-nav-unread">
                        {link.badge > 99 ? "99+" : link.badge}
                      </span>
                    )}
                  </span>
                  <span className="hidden lg:inline">{link.label}</span>
                </Link>
              );
            })}
            
            <div className="w-px h-6 bg-gray-200 mx-2" />
            
            <div className="flex items-center gap-2">
              {/* Support Icon with Badge */}
              <Link
                to="/customer/support"
                data-testid="support-nav-btn"
                className={`p-2 rounded-full transition relative ${
                  isActive("/customer/support")
                    ? "bg-[#4F988E]/10 text-[#4F988E]"
                    : "text-[#717171] hover:bg-gray-100"
                }`}
              >
                <LifeBuoy size={20} />
                {supportUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                    {supportUnreadCount > 99 ? "99+" : supportUnreadCount}
                  </span>
                )}
              </Link>

              {/* Notifications Bell - Prominent when there are unread */}
              <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`p-2 rounded-full transition relative ${
                      notifUnreadCount > 0 
                        ? "bg-[#4F988E] text-white hover:bg-[#3d7a72] animate-pulse" 
                        : "text-[#717171] hover:bg-gray-100"
                    }`} 
                    data-testid="customer-notifications-btn"
                  >
                    <Bell size={20} />
                    {notifUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white">
                        {notifUnreadCount > 99 ? "99+" : notifUnreadCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-96 flex flex-col" sideOffset={8}>
                  {/* Fixed Header */}
                  <div className="bg-white border-b px-4 py-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#222222]">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {notifUnreadCount > 0 && (
                          <button onClick={markAllRead} className="text-xs text-[#4F988E] hover:underline">
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button onClick={deleteAllNotifications} className="text-xs text-red-500 hover:underline flex items-center gap-1">
                            <Trash2 size={12} /> Clear all
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Scrollable Content */}
                  <div className="overflow-y-auto max-h-[350px] flex-1">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-gray-500">
                        <Bell size={32} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    ) : (
                      <div>
                        {notifications.slice(0, 20).map((notif) => (
                          <div
                            key={notif.id}
                            className={`relative px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition ${
                              !notif.read ? "bg-blue-50" : ""
                            }`}
                            onClick={() => {
                              markAsRead(notif.id, notif);
                              setNotifOpen(false);
                              if (notif.type === "message" && notif.booking_id) {
                                navigate(`/customer/chat/${notif.booking_id}?from=bell`);
                              } else if (notif.booking_id) {
                                navigate(`/customer/chat/${notif.booking_id}?from=bell`);
                              } else {
                                navigate("/customer/notifications");
                              }
                            }}
                          >
                            <div className="flex items-start gap-3 pr-8">
                              <div className={`p-2 rounded-full flex-shrink-0 ${
                                notif.type === "message" ? "bg-green-100 text-green-600" :
                                notif.type === "booking" ? "bg-blue-100 text-blue-600" :
                                notif.type === "promotion" ? "bg-purple-100 text-purple-600" :
                                "bg-gray-100 text-gray-600"
                              }`}>
                                <Bell size={16} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-[#222222]">{notif.title}</p>
                                  {!notif.read && (
                                    <span className="w-2 h-2 bg-[#4F988E] rounded-full flex-shrink-0" />
                                  )}
                                </div>
                                <p className="text-xs text-[#717171] line-clamp-2">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => deleteNotification(e, notif.id)}
                              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500 transition"
                              title="Clear notification"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Fixed Footer */}
                  {notifications.length > 0 && (
                    <div className="bg-white border-t flex-shrink-0">
                      <Link
                        to="/customer/notifications"
                        onClick={() => setNotifOpen(false)}
                        className="block px-4 py-3 text-center text-sm text-[#4F988E] font-medium hover:bg-gray-50"
                      >
                        View All Notifications
                      </Link>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/customer/settings"
                data-testid="settings-nav-btn"
                className={`p-2 rounded-full transition ${
                  isActive("/customer/settings")
                    ? "bg-[#4F988E]/10 text-[#4F988E]"
                    : "text-[#717171] hover:bg-gray-100"
                }`}
              >
                <Settings size={20} />
              </Link>

              {/* Invite a Friend Button */}
              <button
                onClick={handleInviteFriend}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#4F988E] to-[#3d7a72] text-white text-sm font-medium rounded-full hover:opacity-90 transition"
                data-testid="invite-friend-btn"
                title="Invite a friend"
              >
                <UserPlus size={16} />
                <span className="hidden xl:inline">Invite</span>
              </button>

              {/* Avatar dropdown — bundles user info + Logout so the
                  logout is always visible even on 1024–1366 px laptops. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-2 py-1.5 bg-[#F7F7F7] rounded-full hover:bg-gray-200 transition"
                    data-testid="customer-avatar-menu"
                    title="Account menu"
                  >
                    <div className="w-8 h-8 bg-[#4F988E] rounded-full flex items-center justify-center text-white text-sm font-semibold">
                      {user?.full_name?.charAt(0)}
                    </div>
                    <span className="hidden xl:inline text-sm font-medium text-[#222222] max-w-[80px] truncate">
                      {user?.full_name?.split(" ")[0]}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" sideOffset={8}>
                  <div className="px-3 py-2.5 border-b">
                    <p className="text-sm font-semibold text-[#222222] truncate">{user?.full_name || "Customer"}</p>
                    {user?.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                  </div>
                  <DropdownMenuItem onClick={() => navigate("/customer/settings")} data-testid="customer-menu-settings">
                    <Settings size={16} className="mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/customer/chats?tab=docs")} data-testid="documents-nav-link">
                    <FileText size={16} className="mr-2 text-[#4F988E]" /> Documents
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/customer/tagged-places")} data-testid="customer-menu-tagged-places">
                    <Tag size={16} className="mr-2 text-[#4F988E]" /> Tagged Places
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleInviteFriend} data-testid="customer-menu-invite">
                    <UserPlus size={16} className="mr-2 text-[#4F988E]" /> Invite a Friend
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 focus:bg-red-50" data-testid="customer-menu-logout">
                    <LogOut size={16} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-full hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="customer-mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Right-side slide-in menu — covers ≈1/3 of the viewport on the
          right so the underlying page stays visible on the left. Shows
          the four utility links the user asked for: Notifications,
          Settings, Support, Invite a Friend. Mounted only while open so
          the slide animation runs every time. */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-[9998] animate-in fade-in-0 duration-200"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="customer-mobile-menu-backdrop"
          />
          <div
            className="fixed top-14 sm:top-16 right-0 bottom-20 sm:bottom-16 w-1/3 min-w-[280px] max-w-[440px] bg-white shadow-2xl border-l border-gray-100 rounded-l-2xl px-6 py-4 space-y-2 overflow-y-auto z-[9999] animate-in slide-in-from-right duration-300"
            style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}
            data-testid="customer-mobile-menu"
          >
          {/* Close (back) button — pinned to the top-right of the menu
              panel so the user always has a one-tap exit. */}
          <div className="flex items-center justify-between pb-2 mb-1 border-b border-gray-100">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Menu</span>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-1 text-sm text-[#4F988E] font-semibold hover:bg-[#4F988E]/10 rounded-full px-3 py-1.5 -mr-2"
              data-testid="customer-mobile-menu-close"
              aria-label="Close menu"
            >
              <X size={18} /> Close
            </button>
          </div>
          {/* Only show items NOT in the footer bar */}
          <Link
            to="/customer/notifications"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 py-3 px-3 rounded-lg transition ${
              isActive("/customer/notifications")
                ? "bg-[#4F988E]/10 text-[#4F988E]"
                : "text-[#222222] hover:bg-gray-50"
            }`}
          >
            <Bell size={20} />
            <span className="font-medium">Notifications</span>
            {unreadCount > 0 && (
              <span className="ml-auto min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Link>
          <Link
            to="/customer/support"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 py-3 px-3 rounded-lg transition ${
              isActive("/customer/support")
                ? "bg-[#4F988E]/10 text-[#4F988E]"
                : "text-[#222222] hover:bg-gray-50"
            }`}
          >
            <LifeBuoy size={20} />
            <span className="font-medium">Support</span>
            {supportUnreadCount > 0 && (
              <span className="ml-auto min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                {supportUnreadCount > 99 ? "99+" : supportUnreadCount}
              </span>
            )}
          </Link>
          <Link
            to="/customer/how-it-works"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="menu-how-it-works"
            className={`flex items-center gap-3 py-3 px-3 rounded-lg transition ${
              isActive("/customer/how-it-works")
                ? "bg-[#4F988E]/10 text-[#4F988E]"
                : "text-[#222222] hover:bg-gray-50"
            }`}
          >
            <BookOpen size={20} />
            <span className="font-medium">How it works</span>
          </Link>
          <Link
            to="/customer/settings"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 py-3 px-3 rounded-lg transition ${
              isActive("/customer/settings")
                ? "bg-[#4F988E]/10 text-[#4F988E]"
                : "text-[#222222] hover:bg-gray-50"
            }`}
          >
            <Settings size={20} />
            <span className="font-medium">Settings</span>
          </Link>
          <Link
            to="/customer/chats?tab=docs"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 py-3 px-3 rounded-lg transition text-[#222222] hover:bg-gray-50"
            data-testid="mobile-menu-documents"
          >
            <FileText size={20} className="text-[#4F988E]" />
            <span className="font-medium">Documents</span>
          </Link>
          <Link
            to="/customer/tagged-places"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 py-3 px-3 rounded-lg transition ${
              isActive("/customer/tagged-places")
                ? "bg-[#4F988E]/10 text-[#4F988E]"
                : "text-[#222222] hover:bg-gray-50"
            }`}
            data-testid="mobile-menu-tagged-places"
          >
            <Tag size={20} />
            <span className="font-medium">Tagged Places</span>
          </Link>
          {/* Invite a Friend — reuses the header's existing WhatsApp /
              clipboard share flow. Closes the drawer before firing so
              the share sheet lands on top of the page, not the menu. */}
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              handleInviteFriend();
            }}
            className="flex items-center gap-3 py-3 px-3 rounded-lg transition text-[#4F988E] hover:bg-[#4F988E]/10 w-full"
            data-testid="customer-mobile-menu-invite"
          >
            <UserPlus size={20} />
            <span className="font-medium">Invite a Friend</span>
          </button>
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              className="w-full rounded-full"
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
          </div>
        </>
      )}
    </header>
  );
}
